import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Pencil, Search, Users, ShieldAlert, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const ROLES = ["Sales", "Delivery", "Production", "Admin", "Other"];

export default function EmployeesList() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    address: "",
    role: "Sales",
    active: true,
  });

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setEmployees(data);
    } catch {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "",
      mobile: "",
      email: "",
      address: "",
      role: "Sales",
      active: true,
    });
    setOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp.id);
    setForm({
      name: emp.name,
      mobile: emp.mobile,
      email: emp.email || "",
      address: emp.address || "",
      role: emp.role,
      active: emp.active,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile are required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/employees/${editing}`, form);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", form);
        toast.success("Employee added");
      }
      setOpen(false);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/employees/${deleteTarget.id}`);
      toast.success("Employee deleted");
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete employee");
    } finally {
      setDeleteTarget(null);
    }
  };

  const filtered = employees.filter((emp) => {
    const term = search.toLowerCase();
    const matchesSearch =
      emp.name.toLowerCase().includes(term) ||
      emp.mobile.includes(term) ||
      (emp.email && emp.email.toLowerCase().includes(term));
    const matchesRole = roleFilter === "all" || emp.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-zinc-500">Administration</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1 flex items-center gap-3">
            <Users className="w-8 h-8 text-zinc-900" /> Employees Master
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Manage your internal staff profiles, delivery personnel, and production sales teams.
          </p>
        </div>
        <Button onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800 h-10" data-testid="add-employee-button">
          <UserPlus className="w-4 h-4 mr-1.5" /> Add Employee
        </Button>
      </div>

      <Card className="p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by name, phone or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 border border-zinc-200"
            data-testid="employee-search"
          />
        </div>
        <div className="w-full md:w-48">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-10">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="text-zinc-500 py-8 text-center">Loading employees…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-zinc-500">
          <ShieldAlert className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
          No employees found matching the search filters.
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="min-w-[640px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((emp) => (
                  <TableRow key={emp.id} className={!emp.active ? "opacity-60" : ""}>
                    <TableCell className="font-medium" data-testid={`emp-name-${emp.id}`}>
                      {emp.name}
                    </TableCell>
                    <TableCell>
                      <Badge variant={emp.active ? "default" : "outline"} className="capitalize">
                        {emp.role}
                      </Badge>
                    </TableCell>
                    <TableCell>{emp.mobile}</TableCell>
                    <TableCell>{emp.email || "—"}</TableCell>
                    <TableCell>
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          emp.active ? "bg-green-100 text-green-800" : "bg-zinc-100 text-zinc-600"
                        }`}
                      >
                        {emp.active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(emp)}
                          data-testid={`edit-emp-${emp.id}`}
                        >
                          <Pencil className="w-4 h-4 text-zinc-600 hover:text-zinc-950" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(emp)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-emp-${emp.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-employee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <span className="font-semibold">{deleteTarget?.name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-employee-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              data-testid="delete-employee-confirm"
            >
              Delete Employee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="employee-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Employee Details" : "Register New Employee"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="employee-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  required
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  data-testid="employee-mobile-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="role">Company Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger id="role" data-testid="employee-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="employee-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                rows={2}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="employee-address-input"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-zinc-50 border rounded-md">
              <div className="space-y-0.5">
                <Label className="text-sm font-semibold">Active Status</Label>
                <p className="text-xs text-zinc-500">Inactive staff are hidden from scheduling dropdowns.</p>
              </div>
              <Switch
                checked={form.active}
                onCheckedChange={(checked) => setForm({ ...form, active: checked })}
                data-testid="employee-active-switch"
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-zinc-900 text-white hover:bg-zinc-800" data-testid="save-employee-button">
                {editing ? "Save Changes" : "Register Employee"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
